import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ApiError, api } from '@/api/client'
import { useConfig } from '@/api/queries'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { ErrorState } from '@/components/ui/EmptyState'
import { LoadingBlock, Spinner } from '@/components/ui/Spinner'
import styles from './AuthGate.module.css'
import { decodeGoogleCredential, loadGoogleIdentityScript } from './googleIdentity'
import { getSession, setSession, useSession } from './session'

/** Renew this long before expiry, so an in-flight request never straddles it. */
const RENEW_MARGIN_MS = 5 * 60 * 1000
/** setTimeout overflows above this. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1
const HOSTED_DOMAIN = 'd-edge.com'

type Status = 'signed-out' | 'checking' | 'signed-in' | 'denied'

/** Shows the app only to an allowlisted D-EDGE Google account, when the API runs with Auth:Mode Google. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { data: config, error } = useConfig()
  if (error) return <ErrorState error={error} />
  if (!config) return <LoadingBlock />
  if (config.auth.mode !== 'Google' || !config.auth.googleClientId) return children
  return <GoogleGate clientId={config.auth.googleClientId}>{children}</GoogleGate>
}

function GoogleGate({ clientId, children }: { clientId: string; children: ReactNode }) {
  const session = useSession()
  const [status, setStatus] = useState<Status>(() => (getSession() ? 'checking' : 'signed-out'))
  const [problem, setProblem] = useState<string | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  // Signing out, or a 401 from any API call, clears the session.
  const current: Status = !session && status !== 'denied' ? 'signed-out' : status

  const acceptCredential = useCallback((credential: string) => {
    const claims = decodeGoogleCredential(credential)
    if (!claims?.email) return
    setSession({ email: claims.email, name: claims.name ?? claims.email, exp: claims.exp, credential })
    setProblem(null)
    setStatus('checking')
  }, [])

  // The API owns the allowlist. A 403 is final: prompting the same account again would loop.
  useEffect(() => {
    if (current !== 'checking' || !session) return
    let cancelled = false
    api.get('/me').then(
      () => {
        if (!cancelled) setStatus('signed-in')
      },
      (error: unknown) => {
        if (cancelled) return
        const status = error instanceof ApiError ? error.status : undefined
        if (status === 403) {
          setProblem(`${session.email} is not authorised to use the cockpit.`)
          setSession(null)
          setStatus('denied')
        } else if (status !== 401) {
          // A network error or a 5xx should not lock out someone who is entitled to the app.
          setStatus('signed-in')
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [current, session])

  // ID tokens have no silent refresh. auto_select + prompt() renews without a click when one Google
  // account is signed in and consent was given; otherwise the sign-in card comes back.
  useEffect(() => {
    if (current !== 'signed-in' || !session) return
    const delay = Math.min(Math.max(session.exp * 1000 - Date.now() - RENEW_MARGIN_MS, 0), MAX_TIMEOUT_MS)
    const timer = window.setTimeout(() => {
      loadGoogleIdentityScript()
        .then(() => {
          window.google?.accounts.id.initialize({ client_id: clientId, hd: HOSTED_DOMAIN, auto_select: true, callback: (r) => acceptCredential(r.credential) })
          window.google?.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) setSession(null)
          })
        })
        .catch(() => setSession(null))
    }, delay)
    return () => window.clearTimeout(timer)
  }, [current, session, clientId, acceptCredential])

  useEffect(() => {
    if (current !== 'signed-out' && current !== 'denied') return
    let cancelled = false
    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !window.google || !buttonRef.current) return
        window.google.accounts.id.initialize({ client_id: clientId, hd: HOSTED_DOMAIN, callback: (r) => acceptCredential(r.credential) })
        window.google.accounts.id.renderButton(buttonRef.current, { theme: 'outline', size: 'large', text: 'signin_with' })
      })
      .catch(() => {
        if (!cancelled) setProblem('Google Sign-In could not be loaded. Check your connection and reload the page.')
      })
    return () => {
      cancelled = true
    }
  }, [current, clientId, acceptCredential])

  if (current === 'signed-in') return children

  return (
    <div className={styles.page}>
      <section className={styles.card} aria-labelledby="sign-in-title">
        <BrandLogo size={48} />
        <h1 id="sign-in-title" className={styles.title}>
          DI Cockpit
        </h1>
        {current === 'checking' ? (
          <p className={styles.note}>
            <Spinner /> Checking your access…
          </p>
        ) : (
          <>
            <p className={styles.note}>Sign in with your D-EDGE Google account to continue.</p>
            {problem && (
              <p className={styles.problem} role="alert">
                {problem}
              </p>
            )}
            <div ref={buttonRef} className={styles.button} />
          </>
        )}
      </section>
    </div>
  )
}
